/**
 * DevNotesProvider — React integration for the Dev Notes annotation system.
 *
 * Drop-in component that lazy-loads the dev-notes IIFE bundle and initializes
 * the developer annotation overlay for dashboard shell apps. Renders nothing.
 *
 * Activation: controlled by `isDevNotesEnabled()` from feature-flags.ts.
 * - URL override: `?devnotes=true` or `?nodevnotes=true`
 * - Env override: `VITE_DEV_NOTES_ENABLED=true`
 *
 * Auth: reads `hub_token` from localStorage (set by portal auth flow) or
 * the `CF_Authorization` cookie (set by Cloudflare Access). Falls back to
 * unauthenticated (org_id = 1 on the backend).
 *
 * Script candidates (tried in order):
 * 1. `/dev-notes/dev-notes.iife.js`  — CF Pages static asset (production)
 * 2. `http://localhost:8395/static/dev-notes.iife.js` — direct dev server
 *
 * @package @plerity/dashboard-shell
 */

import { useEffect } from "react";
import { isDevNotesEnabled } from "../config/feature-flags";

// ── Types ──────────────────────────────────────────────────────────────────

export interface DevNotesProviderProps {
  /** App slug used as context ID for grouping notes (e.g. "crm", "finance") */
  contextId: string;
  /** Context type — always "app" for dashboard shells */
  contextType?: "app" | "template";
  /** Dev Notes API base URL (default: http://localhost:8395) */
  apiUrl?: string;
  /** Override author email (defaults to current user email or "developer") */
  author?: string;
  /**
   * Always activate even without `?devnotes=true` or env var.
   * Use sparingly — prefer env var for dev environments.
   */
  alwaysOn?: boolean;
}

interface DevNotesGlobal {
  init: (config: Record<string, unknown>) => void;
  destroy: () => void;
  state: { getNoteCount: () => number };
}

declare global {
  interface Window {
    DevNotes?: DevNotesGlobal;
    __DEV_NOTES_TOKEN?: string;
    __CURRENT_USER_EMAIL?: string;
    __devNotesPendingScreenshot?: Blob | null;
  }
}

// ── Script loader (module-level singleton) ─────────────────────────────────

let _scriptLoaded = false;
let _scriptLoading: Promise<void> | null = null;

const DEV_NOTES_SCRIPT_CANDIDATES = ["/dev-notes/dev-notes.iife.js", "http://localhost:8395/static/dev-notes.iife.js"];

function _loadDevNotesScript(): Promise<void> {
  if (_scriptLoaded && window.DevNotes) return Promise.resolve();
  if (_scriptLoading) return _scriptLoading;

  _scriptLoading = new Promise<void>((resolve, reject) => {
    const tryLoad = (urls: string[]): void => {
      const url = urls[0];
      if (!url) {
        _scriptLoading = null;
        reject(new Error("[DevNotesProvider] Could not load bundle from any candidate URL"));
        return;
      }

      const script = document.createElement("script");
      script.src = url;
      script.onload = () => {
        _scriptLoaded = true;
        _scriptLoading = null;
        resolve();
      };
      script.onerror = () => {
        script.remove();
        tryLoad(urls.slice(1));
      };
      document.body.appendChild(script);
    };

    tryLoad(DEV_NOTES_SCRIPT_CANDIDATES);
  });

  return _scriptLoading;
}

// ── Auth token detection ───────────────────────────────────────────────────

function _detectAuthToken(): string | undefined {
  // 1. Explicit override (set externally via CF Access JWT extraction)
  if (window.__DEV_NOTES_TOKEN) return window.__DEV_NOTES_TOKEN;

  // 2. Portal hub_token (set by portal auth flow)
  try {
    const ls = localStorage.getItem("hub_token");
    if (ls) return ls;
  } catch {
    // localStorage blocked (private browsing edge case)
  }

  // 3. CF Access JWT from cookie (works same-origin)
  const cfCookie = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith("CF_Authorization="));
  if (cfCookie) return cfCookie.split("=")[1];

  return undefined;
}

// ── Component ──────────────────────────────────────────────────────────────

/**
 * DevNotesProvider — lazy-loads and initializes the Dev Notes annotation
 * system for dashboard shell apps. Renders nothing to the DOM.
 *
 * Include once at the App root (inside `createShell()`, already wired).
 * Shells do NOT need to include this manually — `createShell()` adds it
 * automatically when `VITE_DEV_NOTES_ENABLED=true`.
 *
 * For explicit control (e.g. per-route activation), import and use directly:
 *
 * ```tsx
 * import { DevNotesProvider } from '@plerity/dashboard-shell';
 * <DevNotesProvider contextId="crm" alwaysOn />
 * ```
 */
export function DevNotesProvider({
  contextId,
  contextType = "app",
  apiUrl = "http://localhost:8395",
  author,
  alwaysOn = false,
}: DevNotesProviderProps): null {
  useEffect(() => {
    const active = alwaysOn || isDevNotesEnabled();
    if (!active) return;

    void _loadDevNotesScript()
      .then(() => {
        if (!window.DevNotes) return;

        const token = _detectAuthToken();
        if (token) window.__DEV_NOTES_TOKEN = token;

        window.DevNotes.init({
          contextId,
          contextType,
          apiUrl,
          author: author ?? window.__CURRENT_USER_EMAIL ?? "developer",
          enabled: true,
        });
      })
      .catch((err: unknown) => {
        // Dev tool — non-critical, just warn
        console.warn("[DevNotesProvider] Could not activate dev notes:", err);
      });

    return () => {
      window.DevNotes?.destroy();
    };
  }, [contextId, contextType, apiUrl, author, alwaysOn]);

  return null;
}
